import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Copy, Check, WhatsappLogo } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditClientModal = ({ client, isRegistered, onClose, userId, userRole }) => {
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  
  // Form state for moved clients
  const [formData, setFormData] = useState({
    system_username: client.system_username || '',
    serial_number: client.serial_number || '',
    client_id: client.client_id || '',
    username: client.username || '',
    mobile_number: client.mobile_number || client.phone || '',
    bank_account_number: client.bank_account_number || '',
    bank_name: client.bank_name || '',
    account_holder_name: client.account_holder_name || '',
    ifsc_code: client.ifsc_code || '',
    upi_id: client.upi_id || '',
    address: client.address || '',
    label_casino: client.label_casino || false,
    label_cricket: client.label_cricket || false,
    label_mix: client.label_mix || false,
    more_about_client: client.more_about_client || ''
  });
  
  // Login update state for registered clients
  const [loginData, setLoginData] = useState({
    phone: client.phone || '',
    password: ''
  });

  const [bankVerified, setBankVerified] = useState(client.bank_verified || false);
  const [historyModalField, setHistoryModalField] = useState(null);
  const [historyModalLabel, setHistoryModalLabel] = useState(null);

  const refreshClientDetails = async () => {
    if (!client?.id) return;
    try {
      const res = await axios.get(`${API}/clients/registered/${client.id}`);
      let data = res.data || {};
      
      // Fallback: check client-bank-details if registered client bank data is missing
      if (!data.bank_account_number && !data.ifsc_code) {
        try {
          const cbdRes = await axios.get(`${API}/client-bank-details/${client.id}`);
          if (cbdRes.data?.bank_details) {
            data = { ...data, ...cbdRes.data.bank_details };
          }
        } catch (e) {}
      }

      setFormData(prev => ({
        ...prev,
        system_username: data.system_username || client.system_username || '',
        serial_number: data.serial_number || client.serial_number || '',
        client_id: data.client_id || client.client_id || '',
        username: data.username || client.username || '',
        mobile_number: data.mobile_number || data.phone || client.mobile_number || client.phone || '',
        bank_account_number: data.bank_account_number || client.bank_account_number || '',
        bank_name: data.bank_name || client.bank_name || '',
        account_holder_name: data.account_holder_name || client.account_holder_name || '',
        ifsc_code: data.ifsc_code || client.ifsc_code || '',
        upi_id: data.upi_id || client.upi_id || '',
        address: data.address || client.address || '',
        label_casino: data.label_casino || false,
        label_cricket: data.label_cricket || false,
        label_mix: data.label_mix || false,
        more_about_client: data.more_about_client || client.more_about_client || ''
      }));
      setBankVerified(data.bank_verified || false);
    } catch (err) {
      console.error('Failed to reload client details:', err);
    }
  };

  useEffect(() => {
    if (client) {
      setFormData({
        system_username: client.system_username || '',
        serial_number: client.serial_number || '',
        client_id: client.client_id || '',
        username: client.username || '',
        mobile_number: client.mobile_number || client.phone || '',
        bank_account_number: client.bank_account_number || '',
        bank_name: client.bank_name || '',
        account_holder_name: client.account_holder_name || '',
        ifsc_code: client.ifsc_code || '',
        upi_id: client.upi_id || '',
        address: client.address || '',
        label_casino: client.label_casino || false,
        label_cricket: client.label_cricket || false,
        label_mix: client.label_mix || false,
        more_about_client: client.more_about_client || ''
      });
      if (client.id) {
        refreshClientDetails();
      }
    }
  }, [client?.id]);

  const handleIfscLookup = async (ifscCode) => {
    if (!ifscCode || ifscCode.trim().length < 5) return;
    const cleanIfsc = ifscCode.trim().toUpperCase();
    try {
      const response = await fetch(`https://ifsc.razorpay.com/${encodeURIComponent(cleanIfsc)}`);
      if (response.ok) {
        const data = await response.json();
        if (data && typeof data === 'object') {
          const city = String(data.CITY || data.DISTRICT || '').trim();
          const state = String(data.STATE || '').trim();
          const bankName = String(data.BANK || '').trim();
          const parts = [];
          if (city) parts.push(city);
          if (state) parts.push(state);
          const address = parts.join(', ');

          setFormData(prev => ({
            ...prev,
            ...(address ? { address } : {}),
            ...(bankName && !prev.bank_name ? { bank_name: bankName } : {})
          }));
        }
      }
    } catch (e) {}
  };

  const handleShowHistory = (fieldName, fieldLabel) => {
    setHistoryModalField(fieldName);
    setHistoryModalLabel(fieldLabel);
  };

  // Load linked accounts (same IP / multiple accounts)
  useEffect(() => {
    if (client.same_ip_flag && client.serial_number) {
      const loadLinked = async () => {
        try {
          const res = await axios.get(`${API}/clients/registered`);
          const linked = res.data.filter(
            c => c.serial_number === client.serial_number && c.id !== client.id
          );
          setLinkedAccounts(linked);
        } catch (err) {
          console.error('Failed to load linked accounts:', err);
        }
      };
      loadLinked();
    }
  }, [client.same_ip_flag, client.serial_number, client.id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const copyToClipboard = async (text, fieldName) => {
    if (!text) {
      toast.error('No data to copy');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const copyAllDetails = async () => {
    const formattedText = `Username..${formData.username || 'N/A'}
Mobile number.${formData.mobile_number || client.phone || 'N/A'}
Bank Account No: ${formData.bank_account_number || 'N/A'}
A/c Holder Name: ${formData.account_holder_name || 'N/A'}
IFSC Code: ${formData.ifsc_code || 'N/A'}
Bank Name: ${formData.bank_name || 'N/A'}
Address.. ${formData.address || 'N/A'}
UPI ID.. ${formData.upi_id || 'N/A'}
Amount..`;

    try {
      await navigator.clipboard.writeText(formattedText);
      toast.success('Details copied!');
    } catch (err) {
      toast.error('Failed to copy details');
    }
  };

  // Open WhatsApp with client number (PWA-compatible)
  const openWhatsApp = () => {
    const phone = formData.mobile_number || client.phone;
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    const cleanNumber = phone.replace(/\D/g, '');
    const whatsappNumber = cleanNumber.startsWith('91') ? cleanNumber : `91${cleanNumber}`;
    const link = document.createElement('a');
    link.href = `https://wa.me/${whatsappNumber}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Update client details
      const res = await axios.put(`${API}/clients/update/${client.id}`, formData, {
        params: { 
          user_id: userId,
          is_registered: isRegistered
        }
      });
      if (res.data.address) {
        setFormData(prev => ({ ...prev, address: res.data.address }));
      }
      setBankVerified(res.data.bank_verified || false);
      toast.success('Client details updated!');
      
      setLoading(false);
      onClose();
    } catch (error) {
      console.error('Failed to update client:', error);
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Failed to update client');
      setLoading(false);
    }
  };
  
  const handleLoginUpdate = async () => {
    if (!loginData.password) {
      toast.error('Please enter a new password');
      return;
    }
    
    try {
      setLoading(true);
      
      await axios.put(`${API}/clients/update-login/${client.id}`, loginData);
      toast.success('Login credentials updated successfully!');
      
      setLoading(false);
      setLoginData({ ...loginData, password: '' });
    } catch (error) {
      console.error('Failed to update login:', error);
      toast.error(error.response?.data?.detail || 'Failed to update login');
      setLoading(false);
    }
  };
  
  const CopyButton = ({ text, fieldName }) => (
    <button
      type="button"
      onClick={() => copyToClipboard(text, fieldName)}
      className="p-1.5 hover:bg-wa-hover rounded transition-colors"
      disabled={!text}
    >
      {copiedField === fieldName ? (
        <Check size={18} weight="bold" className="text-green-600" />
      ) : (
        <Copy size={18} weight="bold" className={text ? 'text-wa-text-secondary' : 'text-gray-300'} />
      )}
    </button>
  );
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-wa-panel rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-30 bg-[#008069] text-white p-4 flex items-center justify-between rounded-t-lg shadow-sm">
          <h2 className="text-lg font-bold">
            {isRegistered ? 'Edit Registered Client' : 'Edit Client Details'}
          </h2>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={24} weight="bold" />
          </button>
        </div>
        
        {/* Form Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info Section */}
          <div>
            <h3 className="text-sm font-semibold text-wa-text-primary mb-3 uppercase tracking-wide">
              Basic Information
            </h3>
            
            {/* Serial Number - Non-editable */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-wa-text-primary mb-1">
                Serial Number
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="serial_number"
                  value={formData.serial_number}
                  readOnly
                  className="flex-1 px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-secondary cursor-not-allowed"
                  placeholder="Auto-generated"
                />
                <CopyButton text={formData.serial_number} fieldName="serial_number" />
              </div>
            </div>

            {/* Client Code - Non-editable */}
            {formData.system_username && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-wa-text-primary mb-1">
                  Client code
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="system_username"
                    value={formData.system_username}
                    readOnly
                    className="flex-1 px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-secondary cursor-not-allowed font-semibold"
                  />
                  <CopyButton text={formData.system_username} fieldName="system_username" />
                </div>
              </div>
            )}

            {/* Joining Date - Non-editable */}
            {client.created_at && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-wa-text-primary mb-1">
                  Joining Date
                </label>
                <input
                  type="text"
                  value={new Date(client.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  readOnly
                  className="w-full px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-secondary cursor-not-allowed"
                  data-testid="joining-date"
                />
              </div>
            )}
            
            {/* Client ID */}
            {/* Client ID */}
            {!isRegistered && (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-wa-text-primary">
                    Client ID
                  </label>
                  {userRole === 'super_admin' && (
                    <button
                      type="button"
                      onClick={() => handleShowHistory('client_id', 'Client ID')}
                      className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold border border-blue-200"
                    >
                      H
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="client_id"
                    value={formData.client_id}
                    onChange={handleInputChange}
                    className="flex-1 px-3 py-2 border border-wa-border bg-wa-hover text-wa-text-primary placeholder:text-wa-text-secondary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter client ID"
                  />
                  <CopyButton text={formData.client_id} fieldName="client_id" />
                </div>
              </div>
            )}
            
            {/* Username */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-wa-text-primary">
                  Username
                </label>
                {userRole === 'super_admin' && (
                  <button
                    type="button"
                    onClick={() => handleShowHistory('username', 'Username')}
                    className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold border border-blue-200"
                  >
                    H
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="flex-1 px-3 py-2 border border-wa-border bg-wa-hover text-wa-text-primary placeholder:text-wa-text-secondary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter username"
                />
                <CopyButton text={formData.username} fieldName="username" />
              </div>
            </div>
            
            {/* Mobile Number */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-wa-text-primary">
                  Mobile Number {userRole !== 'super_admin' && <span className="text-xs text-wa-text-secondary">(Admin only)</span>}
                </label>
                {userRole === 'super_admin' && (
                  <button
                    type="button"
                    onClick={() => handleShowHistory('mobile_number', 'Mobile Number')}
                    className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold border border-blue-200"
                  >
                    H
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={userRole === 'super_admin' ? handleInputChange : undefined}
                  readOnly={userRole !== 'super_admin'}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none ${userRole === 'super_admin' ? 'border-wa-border bg-wa-hover text-wa-text-primary placeholder:text-wa-text-secondary focus:ring-2 focus:ring-green-500' : 'border-wa-border bg-wa-hover text-wa-text-secondary cursor-not-allowed'}`}
                  placeholder="Enter mobile number"
                />
                <CopyButton text={formData.mobile_number} fieldName="mobile_number" />
              </div>
            </div>
          </div>
          
          {/* Bank Details Section */}
          <>
            <div className="border border-wa-border p-4 rounded-lg bg-wa-panel-header/20 relative">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-wa-text-primary uppercase tracking-wide">
                    Bank Details
                  </h3>
                  {bankVerified && (
                    <span className="bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5 rounded-full font-bold border border-emerald-300 flex items-center gap-0.5" data-testid="bank-verified-badge">
                      Verified ✓
                    </span>
                  )}
                </div>
                {userRole === 'super_admin' && (
                  <button
                    type="button"
                    onClick={() => handleShowHistory('bank', 'Bank Details')}
                    className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold border border-blue-200"
                  >
                    H
                  </button>
                )}
              </div>
              
              {/* Bank Account Number */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-wa-text-primary mb-1">
                  Bank Account Number
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="bank_account_number"
                    value={formData.bank_account_number}
                    onChange={handleInputChange}
                    className="flex-1 px-3 py-2 border border-wa-border bg-wa-hover text-wa-text-primary placeholder:text-wa-text-secondary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter account number"
                  />
                  <CopyButton text={formData.bank_account_number} fieldName="bank_account_number" />
                </div>
              </div>
              
              {/* Bank Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-wa-text-primary mb-1">
                  Bank Name
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleInputChange}
                    className="flex-1 px-3 py-2 border border-wa-border bg-wa-hover text-wa-text-primary placeholder:text-wa-text-secondary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter bank name"
                  />
                  <CopyButton text={formData.bank_name} fieldName="bank_name" />
                </div>
              </div>
              
              {/* Account Holder Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-wa-text-primary mb-1">
                  Account Holder Name
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="account_holder_name"
                    value={formData.account_holder_name}
                    onChange={handleInputChange}
                    className="flex-1 px-3 py-2 border border-wa-border bg-wa-hover text-wa-text-primary placeholder:text-wa-text-secondary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter account holder name"
                  />
                  <CopyButton text={formData.account_holder_name} fieldName="account_holder_name" />
                </div>
              </div>
              
              {/* IFSC Code */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-wa-text-primary mb-1">
                  IFSC Code
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="ifsc_code"
                    value={formData.ifsc_code}
                    onChange={handleInputChange}
                    onBlur={() => handleIfscLookup(formData.ifsc_code)}
                    className="flex-1 px-3 py-2 border border-wa-border bg-wa-hover text-wa-text-primary placeholder:text-wa-text-secondary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 font-mono uppercase"
                    placeholder="Enter IFSC code"
                  />
                  <CopyButton text={formData.ifsc_code} fieldName="ifsc_code" />
                </div>
              </div>
  
              {/* UPI ID */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-wa-text-primary">
                    UPI ID
                  </label>
                  {userRole === 'super_admin' && (
                    <button
                      type="button"
                      onClick={() => handleShowHistory('upi_id', 'UPI ID')}
                      className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold border border-blue-200"
                    >
                      H
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="upi_id"
                    value={formData.upi_id}
                    onChange={handleInputChange}
                    className="flex-1 px-3 py-2 border border-wa-border bg-wa-hover text-wa-text-primary placeholder:text-wa-text-secondary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter UPI ID (e.g., user@paytm)"
                  />
                  <CopyButton text={formData.upi_id} fieldName="upi_id" />
                </div>
              </div>
  
              {/* Address Field */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-wa-text-primary">
                    Address
                  </label>
                  {userRole === 'super_admin' && (
                    <button
                      type="button"
                      onClick={() => handleShowHistory('address', 'Address')}
                      className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold border border-blue-200"
                    >
                      H
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="flex-1 px-3 py-2 border border-wa-border bg-wa-hover text-wa-text-primary placeholder:text-wa-text-secondary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                    placeholder="Enter address"
                    rows={2}
                  />
                  <CopyButton text={formData.address} fieldName="address" />
                </div>
              </div>
            </div>
            {/* Copy All & WhatsApp Buttons */}
            <div className="mt-6 flex gap-2">
              <button
                type="button"
                onClick={copyAllDetails}
                className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-semibold flex items-center justify-center gap-2"
              >
                <Copy size={20} weight="bold" />
                Copy All Details
              </button>
              <button
                type="button"
                onClick={openWhatsApp}
                className="px-4 py-3 bg-wa-primary-strong text-white rounded-lg hover:bg-wa-primary-strong transition-colors font-semibold flex items-center justify-center gap-2"
              >
                <WhatsappLogo size={20} weight="bold" />
              </button>
            </div>
              <p className="text-xs text-wa-text-secondary mt-2 text-center">
                Copies: Username, Bank Details, Address, UPI
              </p>
          </>
          
          {/* Labels Section */}
          <div>
            <h3 className="text-sm font-semibold text-wa-text-primary mb-3 uppercase tracking-wide">
              Labels
            </h3>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="label_casino"
                  checked={formData.label_casino}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-green-600 border-wa-border rounded focus:ring-green-500"
                />
                <span className="text-sm font-medium text-wa-text-primary">Casino</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="label_cricket"
                  checked={formData.label_cricket}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-green-600 border-wa-border rounded focus:ring-green-500"
                />
                <span className="text-sm font-medium text-wa-text-primary">Cricket</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="label_mix"
                  checked={formData.label_mix}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-green-600 border-wa-border rounded focus:ring-green-500"
                />
                <span className="text-sm font-medium text-wa-text-primary">Mix</span>
              </label>
            </div>
          </div>
          
          {/* More About Client */}
          <div>
            <label className="block text-sm font-medium text-wa-text-primary mb-1">
              More about client
            </label>
            <div className="flex gap-2">
              <textarea
                name="more_about_client"
                value={formData.more_about_client}
                onChange={handleInputChange}
                rows={3}
                className="flex-1 px-3 py-2 border border-wa-border bg-wa-hover text-wa-text-primary placeholder:text-wa-text-secondary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                placeholder="Additional notes about the client..."
              />
              <CopyButton text={formData.more_about_client} fieldName="more_about_client" />
            </div>
          </div>
          


          {/* Linked Accounts (Multiple Account / Same IP) */}
          {linkedAccounts.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <h3 className="text-sm font-semibold text-wa-text-primary mb-3 uppercase tracking-wide">
                Linked Accounts (Multiple Account)
              </h3>
              {linkedAccounts.map((linked) => (
                <div key={linked.id} className="bg-wa-hover rounded-lg p-3 mb-2 border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-orange-200 flex items-center justify-center flex-shrink-0">
                      <span className="text-wa-text-primary font-bold text-xs">{(linked.username || '?')[0].toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-wa-text-primary text-sm">{linked.username || 'N/A'}</p>
                      <p className="text-xs text-wa-text-secondary">{linked.phone || 'No phone'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-xs text-wa-text-secondary">
                    <div>S/N: {linked.serial_number || 'N/A'}</div>
                    <div>ID: {linked.client_id || 'N/A'}</div>
                    {linked.bank_name && <div>Bank: {linked.bank_name}</div>}
                    {linked.account_holder_name && <div>A/c: {linked.account_holder_name}</div>}
                    {linked.bank_account_number && <div>No: {linked.bank_account_number}</div>}
                    {linked.ifsc_code && <div>IFSC: {linked.ifsc_code}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-wa-hover p-4 flex gap-3 rounded-b-lg border-t">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-wa-border rounded-lg hover:bg-wa-hover font-medium text-wa-text-primary"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-wa-primary-strong text-white rounded-lg hover:bg-wa-primary-strong disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
        {historyModalField && (
          <FieldHistoryModal
            customerId={client.id}
            fieldName={historyModalField}
            fieldLabel={historyModalLabel}
            onClose={() => {
              setHistoryModalField(null);
              setHistoryModalLabel(null);
            }}
            onRevertDone={refreshClientDetails}
          />
        )}
      </div>
    </div>
  );
};

const FieldHistoryModal = ({ customerId, fieldName, fieldLabel, onClose, onRevertDone }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/clients/${customerId}/history`, {
        params: { field: fieldName }
      });
      setHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch history:', err);
      toast.error('Failed to load field history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [customerId, fieldName]);

  const handleRevert = async (historyId) => {
    if (!window.confirm('Are you sure you want to revert to this previous value?')) return;
    try {
      await axios.post(`${API}/clients/${customerId}/revert/${historyId}`);
      toast.success('Successfully reverted value!');
      onRevertDone();
      onClose();
    } catch (err) {
      console.error('Failed to revert:', err);
      toast.error('Failed to revert value');
    }
  };

  const renderValue = (val) => {
    if (!val) return <span className="text-gray-400 italic">Empty</span>;
    if (fieldName === 'bank') {
      try {
        const bank = JSON.parse(val);
        return (
          <div className="text-xs bg-wa-hover p-1.5 rounded border text-wa-text-primary">
            <div><strong>A/c:</strong> {bank.bank_account_number || '-'}</div>
            <div><strong>Bank:</strong> {bank.bank_name || '-'}</div>
            <div><strong>Name:</strong> {bank.account_holder_name || '-'}</div>
            <div><strong>IFSC:</strong> {bank.ifsc_code || '-'}</div>
          </div>
        );
      } catch (e) {
        return val;
      }
    }
    return <span className="font-mono text-wa-text-primary">{val}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
      <div className="bg-wa-panel rounded-lg w-full max-w-lg max-h-[80vh] flex flex-col shadow-xl border">
        {/* Header */}
        <div className="p-4 bg-wa-hover border-b flex justify-between items-center rounded-t-lg">
          <h3 className="font-bold text-wa-text-primary">
            Change History: {fieldLabel}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-wa-hover rounded-full">
            <X size={20} className="text-wa-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="text-center py-8 text-wa-text-secondary">Loading history...</div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-wa-text-secondary text-sm">No history logs for this field</div>
          ) : (
            history.map((item) => (
              <div key={item.id} className="p-3 bg-wa-panel border rounded-lg hover:shadow-sm transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-[10px] text-wa-text-secondary font-medium">
                    {new Date(item.modified_at).toLocaleString('en-IN')}
                  </span>
                  <span className="text-[10px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-semibold">
                    By: {item.modified_by_name || 'System'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                  <div>
                    <div className="text-red-600 font-semibold mb-0.5">Previous Value:</div>
                    {renderValue(item.previous_value)}
                  </div>
                  <div>
                    <div className="text-green-600 font-semibold mb-0.5">New Value:</div>
                    {renderValue(item.new_value)}
                  </div>
                </div>
                <button
                  onClick={() => handleRevert(item.id)}
                  className="mt-2 w-full py-1.5 text-center bg-blue-600 hover:bg-blue-700 text-white rounded text-[11px] font-bold transition-colors"
                >
                  Revert to this value
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default EditClientModal;
